import { useState, useEffect } from 'react';

interface OdometerEntryProps {
  type: 'start_day' | 'store_arrival' | 'store_departure' | 'end_day';
  storeName?: string;
  calculatedReading?: number | null;
  lastReading?: number | null;
  /** One-line summary of the leg this reading closes, e.g. "Drove 9:05–9:28 · 23 min". */
  legSummary?: string;
  onSubmit: (reading: number) => void;
  onCancel?: () => void;
  onSkip?: () => void;
  isLoading?: boolean;
}

export default function OdometerEntry({
  type,
  storeName,
  calculatedReading,
  lastReading,
  legSummary,
  onSubmit,
  onCancel,
  onSkip,
  isLoading = false,
}: OdometerEntryProps) {
  const [reading, setReading] = useState<string>('');
  const [useCalculated, setUseCalculated] = useState(false);

  // Pre-fill so the common case is one tap, and so the rep is confirming a
  // number rather than producing one from memory. A GPS-derived suggestion
  // leads when the rep has driven; otherwise the last reading does, which is
  // already correct whenever the vehicle hasn't moved. Either way the value
  // they submit is THEIR reading — the suggestion never stands in for it.
  useEffect(() => {
    if (calculatedReading) {
      setReading(calculatedReading.toFixed(1));
      setUseCalculated(true);
      return;
    }
    if (lastReading != null) {
      setReading(lastReading.toFixed(1));
    }
  }, [calculatedReading, lastReading]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const numReading = parseFloat(reading);
    if (!isNaN(numReading) && numReading >= 0) {
      onSubmit(numReading);
    }
  };

  const handleUseCalculated = () => {
    if (calculatedReading) {
      setReading(calculatedReading.toFixed(1));
      setUseCalculated(true);
    }
  };

  const difference = calculatedReading && reading
    ? parseFloat(reading) - calculatedReading
    : null;

  const getTitle = () => {
    switch (type) {
      case 'start_day':
        return 'Starting Odometer';
      case 'store_arrival':
        return `Odometer at ${storeName || 'Store'}`;
      case 'store_departure':
        return `Odometer leaving ${storeName || 'Store'}`;
      case 'end_day':
        return 'Ending Odometer';
      default:
        return 'Odometer Reading';
    }
  };

  const getDescription = () => {
    switch (type) {
      case 'start_day':
        return 'Enter your vehicle\'s current odometer reading to begin tracking mileage for the day.';
      case 'store_arrival':
        return calculatedReading
          ? 'GPS has estimated your odometer. Adjust if needed.'
          : 'Enter your current odometer reading.';
      case 'store_departure':
        return 'Confirm the reading you are leaving with — it starts your next drive. Edit it if you drove since arriving.';
      case 'end_day':
        return 'Enter your final odometer reading to complete mileage tracking.';
      default:
        return '';
    }
  };

  return (
    <div className="rounded-xl bg-white p-4 shadow-card">
      <div className="mb-4 flex items-start gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-100">
          <svg className="h-5 w-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
        </div>
        <div className="flex-1">
          <h3 className="font-heading font-semibold text-slate-800">{getTitle()}</h3>
          <p className="mt-1 text-sm text-slate-500">{getDescription()}</p>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        {/* The leg this reading closes, so the rep can see what they're
            confirming instead of being asked for a number out of context. */}
        {legSummary && (
          <div className="mb-3 rounded-lg bg-primary-50 p-3 text-sm text-primary-800">{legSummary}</div>
        )}

        {/* Last Reading Reference */}
        {lastReading && (
          <div className="mb-3 rounded-lg bg-slate-50 p-3">
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-500">Last recorded:</span>
              <span className="font-mono font-medium text-slate-700">{lastReading.toLocaleString()} mi</span>
            </div>
          </div>
        )}

        {/* GPS Calculated Suggestion */}
        {calculatedReading && (
          <div className="mb-3 rounded-lg bg-blue-50 p-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <svg className="h-4 w-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                </svg>
                <span className="text-sm text-blue-700">GPS estimate:</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="font-mono font-medium text-blue-800">{calculatedReading.toFixed(1)} mi</span>
                {!useCalculated && (
                  <button
                    type="button"
                    onClick={handleUseCalculated}
                    className="rounded bg-blue-600 px-2 py-1 text-xs font-medium text-white hover:bg-blue-700"
                  >
                    Use
                  </button>
                )}
              </div>
            </div>
            {difference !== null && Math.abs(difference) > 0.1 && (
              <div className={`mt-2 text-xs ${difference > 0 ? 'text-amber-600' : 'text-green-600'}`}>
                {difference > 0 ? '+' : ''}{difference.toFixed(1)} mi from GPS estimate
                {Math.abs(difference) > 5 && (
                  <span className="ml-1 text-amber-700">(large difference - please verify)</span>
                )}
              </div>
            )}
          </div>
        )}

        {/* Odometer Input */}
        <div className="mb-4">
          <label htmlFor="odometer" className="mb-2 block text-sm font-medium text-slate-700">
            Odometer Reading (miles)
          </label>
          <div className="relative">
            <input
              id="odometer"
              type="number"
              inputMode="decimal"
              step="0.1"
              value={reading}
              onChange={(e) => {
                setReading(e.target.value);
                setUseCalculated(false);
              }}
              placeholder="e.g., 45678.9"
              disabled={isLoading}
              className="w-full rounded-lg border border-slate-300 px-4 py-3 pr-12 font-mono text-lg text-slate-900 placeholder-slate-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 disabled:bg-slate-50"
              autoFocus
            />
            <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm text-slate-400">mi</span>
          </div>
          {lastReading && reading && parseFloat(reading) < lastReading && (
            <p className="mt-1 text-xs text-red-500">
              Reading cannot be less than previous ({lastReading.toLocaleString()} mi)
            </p>
          )}
          {/* The input pre-fills with the last reading, so an unchanged value
              here is one habitual tap away from a false 0-mile drive — the rep
              must look at the dash and enter what it says. A genuine same-
              reading hop records through manual entry, where both readings are
              typed (#3618). */}
          {type === 'store_arrival' && lastReading && reading && parseFloat(reading) === lastReading && (
            <p className="mt-1 text-xs text-red-500">
              Reading must be greater than your previous reading ({lastReading.toLocaleString()} mi) — you drove to get here.
            </p>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3">
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              disabled={isLoading}
              className="flex-1 rounded-lg border border-slate-300 px-4 py-3 font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              Cancel
            </button>
          )}
          <button
            type="submit"
            disabled={
              isLoading
              || reading === ''
              || parseFloat(reading) < 0
              || (lastReading ? parseFloat(reading) < lastReading : false)
              || (type === 'store_arrival' && !!lastReading && parseFloat(reading) === lastReading)
            }
            className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-primary-600 px-4 py-3 font-semibold text-white hover:bg-primary-700 disabled:bg-slate-300"
          >
            {isLoading ? (
              <>
                <svg className="h-5 w-5 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                <span>Saving...</span>
              </>
            ) : (
              <span>Confirm Reading</span>
            )}
          </button>
        </div>

        {/* Skip Option */}
        {onSkip && (type === 'start_day' || type === 'end_day') && (
          <button
            type="button"
            onClick={onSkip}
            disabled={isLoading}
            className="mt-3 w-full text-center text-sm text-slate-500 hover:text-slate-700 disabled:opacity-50"
          >
            {type === 'start_day'
              ? 'Skip mileage tracking for today'
              : 'Skip ending odometer'}
          </button>
        )}
      </form>
    </div>
  );
}
